import { memo, useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'

import { FilterList } from '@mui/icons-material'
import { darken, IconButton, lighten, useTheme } from '@mui/material'
import { SmepEvent } from '@nativewaves/nw-cloud-sdk/smep'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Row, Table as ReactTableType, RowSelectionState } from '@tanstack/react-table'
import { VirtualItem } from '@tanstack/react-virtual'
import { omit, pick } from 'lodash-es'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Foldout } from '@shared/components/Foldout'
import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { Table, SelectionToolbar } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import {
  FilterPanel,
  NewDialog,
  RowActions,
  SelectionToolbarActions,
  SuccessPanelActions,
  useAsyncContentCaching,
  useCameraStreamNames,
  useColumns,
  WeekGroupHeader,
  WEEK_GROUP_HEADER_SIZE,
  getWeekKey,
  isWeekendEvent,
} from 'domains/Workspaces/Events'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { eventsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events'
import { eventDashboardRoute } from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'
import { eventQueryKeys } from 'services/queryKeys'

const HEAD_SIZE = 48
// The week band parks right under the sticky head. The head row is content-box with a 4px bottom
// border on top of its height (HeadTableRow), so the band has to clear that too.
const WEEK_BAND_OFFSET = HEAD_SIZE + 4

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const theme = useTheme()

  const navigate = useNavigate({ from: eventsOverviewRoute.fullPath })
  const routeParams = eventsOverviewRoute.useParams()
  const { query, orderByName, orderByState, orderByHealth, orderByEndTime } = eventsOverviewRoute.useSearch()
  const { workspaceId } = routeParams

  const tableInstance = useRef<ReactTableType<SmepEvent>>(undefined)
  const virtualizerInstanceRef = useRef<VirtualItem[]>(undefined)

  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [loadingRows, setLoadingRows] = useState<string[]>([])
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)

  const { workspaceQuery, eventsQuery, setBatchGetEnabled } = useAsyncContentCaching({
    virtualItems: virtualizerInstanceRef.current,
    setLoadingRows,
  })

  const cameraStreamNames = useCameraStreamNames(eventsQuery.data?.flat)
  const columns = useColumns({ cameraStreamNames })

  // A step away from the row colour in whichever direction the mode leaves room for, so the band
  // reads the same in light and dark. Lightness, not hue: colour here already means status.
  const weekendTint =
    theme.palette.mode === 'light'
      ? darken(theme.palette.background.levels[300], 0.075)
      : lighten(theme.palette.background.levels[300], 0.07)

  // Weeks only line up while the server orders by start time, which is also what it does when no sort
  // is chosen. Under any other sort the rows of one week are scattered through the list (end time
  // included: an event running across a week boundary lands among the next week's), and a header
  // over them would be a lie. That holds even with start time in the URL alongside another sort: the
  // headers only allow one at a time, but a hand-edited link can carry several, and which one the
  // server ranks first is not ours to assume.
  const weekGroupingActive = !(orderByName || orderByState || orderByHealth || orderByEndTime)

  // The week the loaded rows end in may carry on into the next page, so its counts are only a floor
  // until that page arrives.
  const openEndedWeekKey = eventsQuery.hasNextPage ? getWeekKey(eventsQuery.data?.flat.at(-1)?.startTime) : undefined

  const rowGrouping = useMemo(
    () =>
      weekGroupingActive
        ? {
            // The ISO year-week, not the row's position, so a week split across two loaded pages
            // stays one run instead of growing a second header at the seam.
            getGroupKey: (row: Row<SmepEvent>) => getWeekKey(row.original.startTime),
            headerSize: WEEK_GROUP_HEADER_SIZE,
            renderHeader: ({ groupKey, rows }: { groupKey: string; rows: Row<SmepEvent>[] }) => (
              <WeekGroupHeader
                key={groupKey}
                startTime={rows[0]?.original.startTime}
                eventCount={rows.length}
                weekendCount={rows.filter((row) => isWeekendEvent(row.original.startTime)).length}
                isOpenEnded={groupKey === openEndedWeekKey}
                stickyOffset={WEEK_BAND_OFFSET}
              />
            ),
            getRowTint: (row: Row<SmepEvent>) => (isWeekendEvent(row.original.startTime) ? weekendTint : undefined),
          }
        : undefined,
    [openEndedWeekKey, weekendTint, weekGroupingActive],
  )

  const handleSuccess = useCallback(
    (data: SmepEvent) => {
      enqueueSnackbar(`${t('domain:Workspace.Event.eventCreated', { name: data.name })}`, {
        description: `${t('domain:Workspace.Event.eventCreatedDescription')}`,
        variant: 'panel',
        persist: true,
        icon: 'success',
        Actions: (props) => <SuccessPanelActions {...props} {...routeParams} eventId={data.id} />,
      })
    },
    [enqueueSnackbar, routeParams, t],
  )

  useEffect(() => {
    if (eventsQuery.isSuccess) {
      tableInstance.current?.setRowSelection((old) =>
        Object.keys(old).reduce((acc, newItem) => {
          const newItemFound = eventsQuery.data.flat.find((task) => task.id === newItem)
          if (newItemFound) {
            return { ...acc, [newItem]: true }
          }
          return acc
        }, {} as RowSelectionState),
      )
    }
  }, [eventsQuery.data?.flat, eventsQuery.isSuccess])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:Workspace.Event.searchEvents')}
          onSearch={({ search }) =>
            navigate({ search: (current) => (search.length ? { ...current, query: search } : omit(current, 'query')) })
          }
          defaultValue={query}
        />

        <IconButton size="medium" onClick={() => setFilterPanelOpen(!filterPanelOpen)}>
          <FilterList fontSize="small" />
        </IconButton>

        <Foldout open={filterPanelOpen} onClose={() => setFilterPanelOpen(false)} placement="end" hideBackdrop={false}>
          <FilterPanel />
        </Foldout>

        <BasicListOperation
          refreshAction={useCallback(() => {
            queryClient.cancelQueries({ queryKey: eventQueryKeys.batchGets() })
            setBatchGetEnabled(false)
            queryClient.invalidateQueries({
              queryKey: eventQueryKeys.list({ workspaceId }).queryKey,
            })
            queryClient.invalidateQueries({ queryKey: eventQueryKeys.billableCounts() })
          }, [queryClient, setBatchGetEnabled, workspaceId])}
          createAction={useCallback(() => {
            setNewDialogOpen(true)
            tableInstance.current?.resetRowSelection()
          }, [])}
        />
      </ActionStrip>

      <Table
        getTableInstance={(instance) => (tableInstance.current = instance)}
        size={52}
        headSize={HEAD_SIZE}
        rowGrouping={rowGrouping}
        data={eventsQuery.data?.flat ?? []}
        columns={columns}
        isInitialLoadingGlobal={workspaceQuery.isLoading || eventsQuery.isLoading}
        isInitialLoadingByRowByID={loadingRows.reduce((acc, row) => ({ ...acc, [row]: [] }), {})}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={eventsOverviewRoute.fullPath}
            to={eventDashboardRoute.to}
            params={{ eventId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['_thumbnail', 'contentRepositoryId', 'alerts', 'id']}
        enableInfinity
        infinityProps={pick(eventsQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        enableVirtualization
        virtualizedProps={{
          getVirtualItems: (instance) => (virtualizerInstanceRef.current = instance),
        }}
        ActionMenuProps={(row) => ({ title: row.original.name, entity: t('entity:smep.event', { count: 1 }) })}
        RowActions={(props) => <RowActions {...props} />}
        renderSelectionToolbar={({ selectedRows }) => (
          <SelectionToolbar
            selectedRows={selectedRows}
            title={t('entity:smep.event', { count: selectedRows.length })}
            discardSelection={() => tableInstance.current?.toggleAllRowsSelected(false)}
            Actions={
              tableInstance.current && (
                <SelectionToolbarActions selectedRows={selectedRows} tableInstance={tableInstance.current} />
              )
            }
          />
        )}
        tableOptions={{
          getRowId: (avcTask) => avcTask.id,
        }}
      />

      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <NewDialog onSuccess={handleSuccess} />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
